import React, { useState, Fragment } from "react";
import moment from "moment";
import { getRelationshipText } from "../helpers";
import { SURVEY_OPEN } from "../config";
import { translationService } from "../utils/translationService";

import Reply from "./Reply";

function PopupComment(props) {
    const admin = props.admin;
    const comment = props.comment;
    const point = props.point;
    const replies = props.replies;
    
    // Track viewport visibility for translation
    React.useEffect(() => {
        const observer = translationService.initializeViewportObserver();
        const element = document.querySelector(`[data-popup-comment-id="${props.id}"]`);
        
        if (observer && element) {
            observer.observe(element);
            return () => observer.disconnect();
        }
    }, [props.id]);
    
    const [isReplying, setReply] = useState(false);
    const [isEditing, setEditing] = useState(false);
    const [editText, setEditText] = useState(comment.comment || comment.text || "");

    let sortedReplies = [];
    if (replies) {
        Object.keys(replies).forEach((key) => {
            replies[key].key = key;
            sortedReplies.push(replies[key]);
        });

        sortedReplies = sortedReplies.sort(function (a, b) {
            return Date.parse(a.submittedOn) - Date.parse(b.submittedOn);
        });
    }

    const handleSaveEdit = () => {
        if (!editText.trim()) {
            alert("Comment cannot be empty");
            return;
        }
        props.editComment(props.id, { comment: editText });
        setEditing(false);
    };

    const handleDeleteComment = () => {
        if (window.confirm("Are you sure you want to delete this comment? This action cannot be undone.")) {
            props.deleteComment(props.id);
        }
    };

    const toggleReply = (e) => {
        if (isReplying) {
            props.postReply(e);

            const repliesDiv = e.target.previousSibling.previousSibling;
            repliesDiv.scrollTop = repliesDiv.scrollHeight;
        }
        setReply(!isReplying);
    };

    // Get category icon - prefer point data over comment data (point is always accurate)
    const category = (point && point.category) || comment.category || "general";
    const type = (point && point.type) || comment.type || "place";
    
    // Normalize dreams_2 to dream for icon URL (must match actual file names)
    const normalizedType = type === "dreams_2" ? "dream" : type;
    const normalizedIconUrl = category === "general" 
        ? `/icons/pin_${normalizedType}.svg`
        : `/icons/${category}_${normalizedType}.svg`;

    const typeLabel = {
        "dream": "Dream",
        "dreams_2": "Dream",
        "place": "Special Place",
        "issue": "Issue"
    };

    // Debug logging
    React.useEffect(() => {
        console.log("PopupComment rendering with:", {
            "point exists": !!point,
            "point.category": point?.category,
            "point.type": point?.type,
            "comment.category": comment.category,
            "comment.type": comment.type,
            "final category": category,
            "final type": type,
            "normalizedType": normalizedType,
            "normalizedIconUrl": normalizedIconUrl,
            "props.id": props.id
        });
    }, [category, type, normalizedType, normalizedIconUrl, point, comment.category, comment.type, props.id]);

    return (
        <div className="comment" data-popup-comment-id={props.id}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
                <img
                    alt=""
                    src={normalizedIconUrl}
                    crossOrigin="anonymous"
                    onError={(e) => {
                        console.error("Icon failed to load:", {
                            src: e.target.src,
                            iconUrl: normalizedIconUrl,
                            category: category,
                            type: type
                        });
                    }}
                    onLoad={() => console.log("Icon loaded successfully:", normalizedIconUrl)}
                    style={{ width: "32px", height: "32px", marginRight: "10px" }}
                />
                <h4 style={{ margin: 0, fontSize: "19px" }}>{category.charAt(0).toUpperCase() + category.slice(1)}: {typeLabel[type] || typeLabel[normalizedType] || "Comment"}</h4>
            </div>
            
            {isEditing ? (
                <Fragment>
                    <textarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        style={{
                            width: "100%",
                            minHeight: "100px",
                            marginBottom: "10px",
                            padding: "8px",
                            fontSize: "14px",
                            fontFamily: "inherit",
                            border: "1px solid #ccc",
                            borderRadius: "4px"
                        }}
                    />
                    <button
                        className="button--primary"
                        onClick={handleSaveEdit}
                        style={{ marginRight: "8px" }}
                    >
                        Save Changes
                    </button>
                    <button
                        className="button--secondary"
                        onClick={() => {
                            setEditText(comment.comment || comment.text || "");
                            setEditing(false);
                        }}
                    >
                        Cancel
                    </button>
                </Fragment>
            ) : (
                <Fragment>
                    <h3 className="commentContent">{comment.comment || comment.text || "No text"}</h3>
                    <p className="commentBy">
                        {"by "}
                        {(comment.commentBy || comment.name || "Anonymous").split(" ")[0]}
                    </p>
                    <p className="commentDate">
                        {comment.submittedOn ? moment(comment.submittedOn).fromNow() : "Date unknown"}
                    </p>
                    <p className="commentAbout">
                        {getRelationshipText(comment.live, comment.work, comment.visit)}
                    </p>
                </Fragment>
            )}

            <div className="postedReplies">
                {replies &&
                    sortedReplies.map((reply, i) => (
                        <Reply
                            key={i}
                            replyId={reply.key}
                            commentId={props.id}
                            admin={admin}
                            hideReply={props.hideReply}
                            content={{
                                ...reply,
                            }}
                        />
                    ))}
            </div>
            {SURVEY_OPEN && (
                <Fragment>
                    {isReplying && (
                        <Fragment>
                                <div className="replyControls">
                                    
                                    <textarea
                                        id="replyText"
                                        className="replyText"
                                        placeholder="Your reply"
                                    ></textarea>
                                    
                                    <div className="replyReason">
                                        <label>
                                            What's your relationship to Herndon?
                                        </label>
                                        <div className="replyChecks">
                                            <div className="checkGroup">
                                                <input
                                                    id="replyLive"
                                                    type="checkbox"
                                                />
                                                <label>Live</label>
                                            </div>
                                            <div className="checkGroup">
                                                <input
                                                    id="replyWork"
                                                    type="checkbox"
                                                />
                                                <label>Work</label>
                                            </div>
                                            <div className="checkGroup">
                                                <input
                                                    id="replyVisit"
                                                    type="checkbox"
                                                />
                                                <label>Visit</label>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="replyName">
                                        <label>What's your name? (optional)</label>
                                        <input id="replyName" type="text" />
                                    </div>
                                    <div className="replyEmail">
                                        <label>Your email (optional)</label>
                                        <input id="replyEmail" type="email" placeholder="your@email.com" />
                                    </div>
                                </div>
                                <button
                                    className="postReply button--primary"
                                    data-id={props.id}
                                    onClick={(e) => toggleReply(e)}
                                >
                                    Post Reply
                                </button>
                                <button
                                    className="replyCancel"
                                    onClick={() => setReply(false)}
                                >
                                    Cancel
                                </button>
                            </Fragment>
                        )}
                        {!isReplying && (
                            <button
                                className="postReply button--primary"
                                data-id={props.id}
                                onClick={toggleReply}
                            >
                                Reply
                            </button>
                        )}
                </Fragment>
            )}

            {!isEditing && (
                <div className="admin">
                    {props.isUserComment && (
                        <Fragment>
                            <button
                                className="edit button--secondary"
                                onClick={() => setEditing(true)}
                            >
                                Edit
                            </button>
                            <button
                                className="delete button--secondary"
                                onClick={handleDeleteComment}
                            >
                                Delete
                            </button>
                        </Fragment>
                    )}
                    {admin && (
                        <Fragment>
                            <button
                                className="hide button--admin"
                                onClick={() => props.hideComment(props.id)}
                            >
                                {comment.hidden ? "Unhide" : "Hide"}
                            </button>
                            <button
                                className="feature button--admin"
                                onClick={() => props.featureComment(props.id)}
                            >
                                {comment.featured ? "Unfeature" : "Feature"}
                            </button>
                        </Fragment>
                    )}
                </div>
            )}
        </div>
    );
}

export default PopupComment;
